import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { EnrollmentFormData } from '../../../types/enrollment.types';

interface ReviewSummaryStepProps {
  formData: EnrollmentFormData;
  /** If true, show only basic info (no health questionnaire sections) */
  simplified?: boolean;
}

export default function ReviewSummaryStep({ formData, simplified = false }: ReviewSummaryStepProps) {
  const { t } = useTranslation();

  const renderValue = (value: string | boolean | number | string[] | undefined) => {
    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      return <Typography color="text.secondary">{t('common.notProvided')}</Typography>;
    }
    if (typeof value === 'boolean') {
      return value ? t('common.yes') : t('common.no');
    }
    if (Array.isArray(value)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((item) => (
            <Chip key={item} label={t(`${item}`)} size="small" />
          ))}
        </Box>
      );
    }
    return value;
  };

  const renderSection = (
    titleKey: string,
    items: Array<{ labelKey: string; value: any }>
  ) => (
    <Accordion defaultExpanded sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t(titleKey)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <List dense disablePadding>
          {items.map((item, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemText
                primary={t(item.labelKey)}
                secondary={renderValue(item.value)}
                primaryTypographyProps={{ color: 'text.secondary', fontSize: '0.9rem' }}
                secondaryTypographyProps={{ color: 'text.primary', fontSize: '1rem' }}
              />
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t('step.review')}
      </Typography>

      <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon />
          <Typography variant="body1">
            {t('step.review.description')}
          </Typography>
        </Box>
      </Paper>

      {/* Personal Info */}
      {renderSection('step.personal', [
        { labelKey: 'field.lastName', value: formData.lastName },
        { labelKey: 'field.firstName', value: formData.firstName },
        { labelKey: 'field.dateOfBirth', value: formData.dateOfBirth },
        { labelKey: 'field.gender', value: formData.gender ? t(`field.gender.${formData.gender.toLowerCase()}`) : '' },
        { labelKey: 'field.hkId', value: formData.hkIdPrefix },
        { labelKey: 'field.occupation', value: formData.occupation },
      ])}

      {/* Contact Info */}
      {renderSection('step.contact', [
        { labelKey: 'field.phone', value: formData.phone },
        { labelKey: 'field.email', value: formData.email },
        { labelKey: 'field.referralSource', value: formData.referralSource ? t(`field.referralSource.${formData.referralSource}`) : '' },
        { labelKey: 'field.referredBy', value: formData.referredBy },
      ])}

      {/* Emergency Contact */}
      {renderSection('step.emergency', [
        { labelKey: 'field.emergency.name', value: formData.emergencyName },
        { labelKey: 'field.emergency.phone', value: formData.emergencyPhone },
        { labelKey: 'field.emergency.relationship', value: formData.emergencyRelationship },
      ])}

      {/* Health sections - only show if not simplified */}
      {!simplified && (
        <>
          {/* Body Metrics */}
          {renderSection('step.bodyMetrics', [
            {
              labelKey: 'field.height',
              value: formData.heightValue
                ? `${formData.heightValue} ${t(`unit.${formData.heightUnit}`)}`
                : '',
            },
            {
              labelKey: 'field.weight',
              value: formData.weightValue
                ? `${formData.weightValue} ${t(`unit.${formData.weightUnit}`)}`
                : '',
            },
          ])}

          {/* Chief Complaint */}
          {renderSection('step.chiefComplaint', [
            {
              labelKey: 'field.symptomOnset',
              value:
                formData.symptomOnsetYear && formData.symptomOnsetMonth
                  ? `${formData.symptomOnsetYear}/${formData.symptomOnsetMonth}`
                  : '',
            },
            { labelKey: 'field.chiefComplaint', value: formData.chiefComplaint },
            { labelKey: 'field.mainProblem', value: formData.mainProblemToResolve },
          ])}

          {/* Health Conditions */}
          {renderSection('step.healthConditions', [
            {
              labelKey: 'conditions.title',
              value: formData.medicalConditions.length > 0
                ? formData.medicalConditions.map((c) => t(`conditions.${c}`)).join(', ')
                : '',
            },
            {
              labelKey: 'medicalHistory.surgery',
              value: formData.surgeryHistory ? formData.surgeryDetails || t('common.yes') : '',
            },
            {
              labelKey: 'medicalHistory.injury',
              value: formData.injuryHistory ? formData.injuryDetails || t('common.yes') : '',
            },
          ])}

          {/* Pain Assessment */}
          {renderSection('step.painAssessment', [
            {
              labelKey: 'pain.location.title',
              value: formData.painLocations.length > 0
                ? formData.painLocations
                    .map((loc) => {
                      const areaName = t(`pain.location.${loc.area}`);
                      return loc.side ? `${areaName} (${t(`pain.side.${loc.side}`)})` : areaName;
                    })
                    .join(', ')
                : '',
            },
            { labelKey: 'pain.severity', value: formData.painSeverity > 0 ? `${formData.painSeverity}/10` : '' },
            { labelKey: 'pain.frequency.title', value: formData.painFrequency ? t(`pain.frequency.${formData.painFrequency}`) : '' },
          ])}
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" color="text.secondary" textAlign="center">
        {t('step.review.continueToConsent')}
      </Typography>
    </Box>
  );
}
